import { getActiveInitRoute, getLoginUser } from './helper';
import {
    GetOrganisations,
    StoreOrganisation,
} from '../store/actions/settingsActionCreator';

/**
 * MPIN gate policy — the single home for everything that decides whether a
 * logged-in StaffPay user still owes an MPIN step (setup / verify / change).
 *
 * Background (backend design doc §4.3): the JWT is issued and persisted at
 * OTP success, BEFORE the MPIN step. The reload gate lives in MainLayout,
 * which reads localStorage synchronously during render — so the armed-gate
 * marker must be localStorage, not redux (rehydration is async). This is
 * the same shape as the existing CONSENT_PENDING precedent, extended to
 * carry the server's MPIN context as JSON.
 *
 * SECURITY: this module stores flow metadata only. Raw MPIN values must
 * never be written to localStorage, redux, or logs.
 */

export const MPIN_PENDING_KEY = 'MPIN_PENDING';

export type MpinState =
    'SETUP_REQUIRED' | 'VERIFY_REQUIRED' | 'CHANGE_REQUIRED' | 'NOT_APPLICABLE';

export interface MpinContext {
    state: MpinState;
    length: number;
    message: string | null;
    attemptsRemaining: number | null;
    /** Absolute epoch ms — a persisted relative duration goes stale. */
    blockedUntilMs: number | null;
}

const GATED_STATES: MpinState[] = [
    'SETUP_REQUIRED',
    'VERIFY_REQUIRED',
    'CHANGE_REQUIRED',
];

/** The one predicate for "this state demands an MPIN step". */
export const isMpinGateRequired = (
    state?: string | null,
    required: boolean = true,
): state is MpinState =>
    !!required && GATED_STATES.includes(state as MpinState);

const normalizeLength = (value: unknown): number => {
    const length = Number(value);
    return Number.isFinite(length) && length >= 4 && length <= 8 ? length : 6;
};

const toBlockedUntilMs = (blockedForSeconds: unknown): number | null => {
    const seconds = Number(blockedForSeconds);
    return Number.isFinite(seconds) && seconds > 0
        ? Date.now() + seconds * 1000
        : null;
};

const toAttempts = (value: unknown): number | null => {
    const attempts = Number(value);
    return Number.isFinite(attempts) ? attempts : null;
};

export const isMpinPending = (): boolean =>
    !!localStorage.getItem(MPIN_PENDING_KEY);

export const readMpinContext = (): MpinContext | null => {
    try {
        const raw = localStorage.getItem(MPIN_PENDING_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!isMpinGateRequired(parsed?.state)) return null;
        return {
            state: parsed.state,
            length: normalizeLength(parsed.length),
            message: parsed.message ?? null,
            attemptsRemaining: toAttempts(parsed.attemptsRemaining),
            blockedUntilMs: Number.isFinite(Number(parsed.blockedUntilMs))
                ? Number(parsed.blockedUntilMs)
                : null,
        };
    } catch (_error) {
        return null;
    }
};

export const writeMpinContext = (context: MpinContext): void => {
    localStorage.setItem(MPIN_PENDING_KEY, JSON.stringify(context));
};

export const clearMpinContext = (): void => {
    localStorage.removeItem(MPIN_PENDING_KEY);
};

/**
 * Captures the MPIN block from the LOGIN response (PREFIXED field names:
 * mpinState / mpinLength / mpinMessage / mpinAttemptsRemaining /
 * mpinBlockedForSeconds). Arms the gate and returns true when a step is
 * owed; clears any stale gate and returns false otherwise (NOT_APPLICABLE,
 * fields absent on older backends / non-cohort orgs).
 *
 * Deliberately called from the login success callback only — never from
 * setUserInfo, which is shared with refreshToken/SSO and must not be able
 * to re-arm or clear the gate as a side effect of a token refresh.
 */
export const captureLoginMpin = (loginResponse: any): boolean => {
    const state = loginResponse?.mpinState;
    const required = loginResponse?.mpinRequired ?? true;
    if (!isMpinGateRequired(state, required)) {
        clearMpinContext();
        return false;
    }
    writeMpinContext({
        state,
        length: normalizeLength(loginResponse?.mpinLength),
        message: loginResponse?.mpinMessage ?? null,
        attemptsRemaining: toAttempts(loginResponse?.mpinAttemptsRemaining),
        blockedUntilMs: toBlockedUntilMs(loginResponse?.mpinBlockedForSeconds),
    });
    return true;
};

/**
 * Translates a GET /user/mpin/status response (UNPREFIXED field names:
 * state / mpinLength / message / attemptsRemaining / blockedForSeconds)
 * into an MpinContext. The ONLY place the naming dialects meet. Returns
 * null when the state is not gated (NOT_APPLICABLE / unknown).
 */
export const contextFromStatus = (status: any): MpinContext | null => {
    if (!isMpinGateRequired(status?.state)) return null;
    return {
        state: status.state,
        length: normalizeLength(status?.mpinLength),
        message: status?.message ?? null,
        attemptsRemaining: toAttempts(status?.attemptsRemaining),
        blockedUntilMs: toBlockedUntilMs(status?.blockedForSeconds),
    };
};

/**
 * The one post-auth entry into the app — organisation fetch + persist, then
 * route to the first permitted screen. Mirrors Login.tsx's proceedToApp:
 * an organisation-fetch failure still routes in.
 */
export const enterApp = (dispatch: any, history: any): void => {
    const route = () => history.replace(`/${getActiveInitRoute()}`);
    dispatch(
        GetOrganisations(
            getLoginUser()?.organizationId,
            (resp: any) => {
                try {
                    localStorage.setItem(
                        'ORG_INFO',
                        window.btoa(
                            unescape(encodeURIComponent(JSON.stringify(resp))),
                        ),
                    );
                    localStorage.setItem('org', resp?.name ?? '');
                    dispatch(StoreOrganisation(resp));
                } catch (_error) {
                    // Org bookkeeping must never block entry.
                }
                route();
            },
            route,
        ),
    );
};
